/**
 * Product Inventory Page
 * Displays all products with filtering and bulk actions.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { productService, Product, ProductBatch } from '../services/product.service';
import { formatBatchName } from '../lib/utils';
import { mapStatusToBadge } from '../lib/status';
import { Alert, Button, FilterBar, PageHeader, Select, StatusBadge } from '../components/ui';

type SortValue = 'created-desc' | 'created-asc' | 'serial-asc' | 'serial-desc';
type ViewValue = 'list' | 'grid';

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export default function ProductInventoryPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(new Set());
  const [activatingProducts, setActivatingProducts] = useState<Set<string>>(new Set());
  const [batches, setBatches] = useState<ProductBatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortValue>('created-desc');
  const [viewMode, setViewMode] = useState<ViewValue>('list');

  const [filters, setFilters] = useState({
    status: '',
    activationStatus: '',
    batchId: '',
  });

  useEffect(() => {
    const manufacturerId = user?.manufacturerId;
    if (!manufacturerId) return;

    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [productsData, batchesData] = await Promise.all([
          productService.getManufacturerInventory(manufacturerId, {
            status: filters.status || undefined,
            activationStatus: filters.activationStatus || undefined,
            batchId: filters.batchId || undefined,
          }),
          productService.getManufacturerBatches(manufacturerId, 100),
        ]);

        setProducts(productsData);
        setBatches(batchesData);
      } catch (err: any) {
        console.error('Failed to load inventory:', err);
        setError(err.response?.data?.error || 'Failed to load product inventory');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [filters.activationStatus, filters.batchId, filters.status, user?.manufacturerId]);

  useEffect(() => {
    setSelectedProducts((prev) => {
      const available = new Set(products.map((product) => product.id));
      const next = new Set(Array.from(prev).filter((id) => available.has(id)));
      return next;
    });
  }, [products]);

  const batchNameById = useMemo(() => {
    const map = new Map<string, string>();
    batches.forEach((batch) => {
      map.set(batch.id, formatBatchName(batch.productName, batch.createdAt, batch.batchTitle));
    });
    return map;
  }, [batches]);

  const batchSelectOptions = useMemo(
    () => [
      { value: '', label: 'All Batches' },
      ...batches.map((batch) => ({
        value: batch.id,
        label: `${formatBatchName(batch.productName, batch.createdAt, batch.batchTitle)} (${batch.serialStart}-${batch.serialEnd})`,
      })),
    ],
    [batches]
  );

  const statusOptions = useMemo(
    () => [
      { value: 'all', label: 'All', count: products.length },
      { value: 'active', label: 'Active', count: products.filter((product) => product.status === 'active').length },
      {
        value: 'consumed',
        label: 'Consumed',
        count: products.filter((product) => product.status === 'consumed').length,
      },
      { value: 'expired', label: 'Expired', count: products.filter((product) => product.status === 'expired').length },
      { value: 'revoked', label: 'Revoked', count: products.filter((product) => product.status === 'revoked').length },
    ],
    [products]
  );

  const visibleProducts = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    const filtered = products.filter((product) => {
      if (!normalizedQuery) return true;
      const batchName = batchNameById.get(product.batchId) ?? 'Unknown';
      return (
        String(product.serialNumber).includes(normalizedQuery) ||
        batchName.toLowerCase().includes(normalizedQuery) ||
        product.status.toLowerCase().includes(normalizedQuery) ||
        product.activationStatus.toLowerCase().includes(normalizedQuery)
      );
    });

    return filtered.sort((a, b) => {
      if (sortBy === 'serial-asc') return a.serialNumber - b.serialNumber;
      if (sortBy === 'serial-desc') return b.serialNumber - a.serialNumber;
      if (sortBy === 'created-asc') return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }, [batchNameById, products, searchQuery, sortBy]);

  const hasActiveFilters =
    Boolean(filters.status) || Boolean(filters.activationStatus) || Boolean(filters.batchId) || Boolean(searchQuery.trim());

  const allVisibleSelected =
    visibleProducts.length > 0 && visibleProducts.every((product) => selectedProducts.has(product.id));

  const handleFilterChange = (name: 'status' | 'activationStatus' | 'batchId', value: string) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      activationStatus: '',
      batchId: '',
    });
    setSearchQuery('');
    setSelectedProducts(new Set());
  };

  const toggleProductSelection = (productId: string) => {
    setSelectedProducts((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  };

  const toggleSelectAllVisible = () => {
    setSelectedProducts((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        visibleProducts.forEach((product) => next.delete(product.id));
      } else {
        visibleProducts.forEach((product) => next.add(product.id));
      }
      return next;
    });
  };

  const reloadInventory = async () => {
    const manufacturerId = user?.manufacturerId;
    if (!manufacturerId) return;

    const productsData = await productService.getManufacturerInventory(manufacturerId, {
      status: filters.status || undefined,
      activationStatus: filters.activationStatus || undefined,
      batchId: filters.batchId || undefined,
    });
    setProducts(productsData);
  };

  const handleBulkActivate = async () => {
    if (selectedProducts.size === 0) {
      setError('Please select products to activate');
      return;
    }

    try {
      setError(null);
      await productService.bulkActivateProducts(Array.from(selectedProducts));
      setSelectedProducts(new Set());
      await reloadInventory();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to activate products');
    }
  };

  const handleActivateSingle = async (productId: string) => {
    try {
      setError(null);
      setActivatingProducts((prev) => {
        const next = new Set(prev);
        next.add(productId);
        return next;
      });
      await productService.activateProduct(productId);
      await reloadInventory();
    } catch (err: any) {
      console.error('Failed to activate product:', err);
      setError(err.response?.data?.error || 'Failed to activate product');
    } finally {
      setActivatingProducts((prev) => {
        const next = new Set(prev);
        next.delete(productId);
        return next;
      });
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
      <PageHeader
        title="Product Registry"
        description="View and manage individual product units minted under your batches. For batch-level history and actions, use Batch Management."
        actions={
          <Button onClick={() => navigate('/products/create-batch')}>
            Open Batch Management
          </Button>
        }
      />

      <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
        <p className="text-sm font-medium text-emerald-900">Need batch-level actions instead of product-level actions?</p>
        <p className="mt-1 text-sm text-emerald-700">
          Open <span className="font-semibold">Batch Management</span> to review past batches, create a new batch, and open packaging or batch detail workflows.
        </p>
      </div>

      <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
        <p className="text-sm font-medium text-blue-900">Packaging setup for bulk shipments</p>
        <p className="mt-1 text-sm text-blue-700">
          Configure cases and pallets from the batch details page using <span className="font-semibold">Configure Packaging</span>.
        </p>
      </div>

      {error && <Alert variant="error">{error}</Alert>}

      <FilterBar
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder="Search by serial number, status, or batch..."
        statusOptions={statusOptions}
        selectedStatus={filters.status || 'all'}
        onStatusChange={(value) => handleFilterChange('status', value === 'all' ? '' : value)}
        sortOptions={[
          { value: 'created-desc', label: 'Sort: Latest Created' },
          { value: 'created-asc', label: 'Sort: Oldest Created' },
          { value: 'serial-asc', label: 'Sort: Serial Low to High' },
          { value: 'serial-desc', label: 'Sort: Serial High to Low' },
        ]}
        selectedSort={sortBy}
        onSortChange={(value) => setSortBy(value as SortValue)}
        viewOptions={[
          { value: 'list', label: 'View: List' },
          { value: 'grid', label: 'View: Grid' },
        ]}
        selectedView={viewMode}
        onViewChange={(value) => setViewMode(value as ViewValue)}
      />

      <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-2 lg:max-w-3xl">
            <Select
              label="Batch"
              value={filters.batchId}
              onChange={(event) => handleFilterChange('batchId', event.target.value)}
              options={batchSelectOptions}
            />
            <Select
              label="Activation Status"
              value={filters.activationStatus}
              onChange={(event) => handleFilterChange('activationStatus', event.target.value)}
              options={[
                { value: '', label: 'All Statuses' },
                { value: 'CREATED', label: 'Created' },
                { value: 'ACTIVATED', label: 'Activated' },
                { value: 'CONSUMED', label: 'Consumed' },
              ]}
            />
          </div>
          {hasActiveFilters && (
            <Button variant="outline" onClick={clearFilters}>
              Clear All Filters
            </Button>
          )}
        </div>
      </section>

      <section className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="flex flex-col gap-3 border-b border-gray-200 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300 text-blue-600"
              checked={allVisibleSelected}
              onChange={toggleSelectAllVisible}
              aria-label="Select all visible products"
            />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Products ({visibleProducts.length})</h2>
              <p className="text-sm text-gray-600">{selectedProducts.size} selected</p>
            </div>
          </div>
          <Button variant="primary" disabled={selectedProducts.size === 0} onClick={handleBulkActivate}>
            Activate Selected
          </Button>
        </div>

        {loading ? (
          <div className="px-6 py-12 text-center">
            <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600" />
            <p className="text-gray-600">Loading products...</p>
          </div>
        ) : visibleProducts.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <h3 className="mb-2 text-lg font-medium text-gray-900">No Products Found</h3>
            <p className="mb-4 text-gray-600">
              {hasActiveFilters ? 'Try adjusting your search or filters.' : 'Create your first product batch to get started.'}
            </p>
            {!hasActiveFilters && (
              <Button onClick={() => navigate('/products/create-batch')}>
                Open Batch Management
              </Button>
            )}
          </div>
        ) : viewMode === 'list' ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200" aria-label="Product registry list">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-gray-300 text-blue-600"
                      checked={allVisibleSelected}
                      onChange={toggleSelectAllVisible}
                      aria-label="Select all visible products"
                    />
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Serial Number
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Product Name
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Activation
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Created
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {visibleProducts.map((product) => {
                  const activationBadge = mapStatusToBadge(product.activationStatus);
                  const statusBadge = mapStatusToBadge(product.status);
                  const batchName = batchNameById.get(product.batchId) ?? 'Unknown';
                  const isActivating = activatingProducts.has(product.id);

                  return (
                    <tr key={product.id} className="hover:bg-gray-50">
                      <td className="whitespace-nowrap px-6 py-4 text-sm">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-gray-300 text-blue-600"
                          checked={selectedProducts.has(product.id)}
                          onChange={() => toggleProductSelection(product.id)}
                          aria-label={`Select product serial ${product.serialNumber}`}
                        />
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">#{product.serialNumber}</td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">{batchName}</td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <StatusBadge tone={activationBadge.tone} size="sm">
                          {activationBadge.label}
                        </StatusBadge>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <StatusBadge tone={statusBadge.tone} size="sm">
                          {statusBadge.label}
                        </StatusBadge>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">{formatDate(product.createdAt)}</td>
                      <td className="whitespace-nowrap px-6 py-4 text-right text-sm font-medium">
                        <div className="inline-flex items-center gap-3">
                          {product.activationStatus === 'CREATED' && (
                            <button
                              type="button"
                              onClick={() => handleActivateSingle(product.id)}
                              disabled={isActivating}
                              className="text-green-600 hover:text-green-800 disabled:cursor-not-allowed disabled:text-green-400"
                            >
                              {isActivating ? 'Activating...' : 'Activate'}
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => navigate(`/products/batch/${product.batchId}`)}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            View Batch
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 xl:grid-cols-3">
            {visibleProducts.map((product) => {
              const activationBadge = mapStatusToBadge(product.activationStatus);
              const statusBadge = mapStatusToBadge(product.status);
              const batchName = batchNameById.get(product.batchId) ?? 'Unknown';
              const isActivating = activatingProducts.has(product.id);

              return (
                <article key={product.id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                  <div className="mb-3 flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-gray-300 text-blue-600"
                        checked={selectedProducts.has(product.id)}
                        onChange={() => toggleProductSelection(product.id)}
                        aria-label={`Select product serial ${product.serialNumber}`}
                      />
                      <p className="text-sm font-semibold text-gray-900">#{product.serialNumber}</p>
                    </div>
                    <StatusBadge tone={statusBadge.tone} size="sm">
                      {statusBadge.label}
                    </StatusBadge>
                  </div>

                  <p className="text-sm font-medium text-gray-900">{batchName}</p>
                  <p className="mt-1 text-xs text-gray-500">Created {formatDate(product.createdAt)}</p>

                  <div className="mt-3">
                    <StatusBadge tone={activationBadge.tone} size="sm">
                      Activation: {activationBadge.label}
                    </StatusBadge>
                  </div>

                  <div className="mt-4 flex items-center gap-3">
                    {product.activationStatus === 'CREATED' && (
                      <button
                        type="button"
                        onClick={() => handleActivateSingle(product.id)}
                        disabled={isActivating}
                        className="text-sm font-medium text-green-600 hover:text-green-800 disabled:cursor-not-allowed disabled:text-green-400"
                      >
                        {isActivating ? 'Activating...' : 'Activate'}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => navigate(`/products/batch/${product.batchId}`)}
                      className="text-sm font-medium text-blue-600 hover:text-blue-800"
                    >
                      View Batch
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
